import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AdminStats } from '@kome/shared';
import { formatKakaoTime } from '@kome/shared';
import adminApi, { AdminApiError } from '@/lib/api';
import TopBar from '@/components/TopBar';
import StatCard from '@/components/StatCard';
import Card from '@/components/Card';
import Spinner from '@/components/Spinner';
import Badge from '@/components/Badge';
import Button from '@/components/Button';

/**
 * 실시간 모니터링 — stats() 폴링으로 스냅샷을 모아 클라이언트에서 추이를 근사한다.
 * 서버 stats 엔드포인트는 시계열을 주지 않으므로, 페이지가 열려 있는 동안 수집한
 * 표본으로 인라인 SVG 스파크라인 / div 막대 차트를 그린다(외부 차트 라이브러리 미사용).
 */

/** 폴링 주기 (5초) — "실시간 느낌". 백그라운드 탭에서는 건너뛴다. */
const POLL_INTERVAL_MS = 5_000;
/** 추이 버퍼 최대 표본 수 (5초 × 60 ≈ 5분 창) */
const MAX_SAMPLES = 60;

/** 한 번의 폴링으로 수집한 스냅샷 표본 */
interface Sample {
  at: number;
  /** 현재 접속자 */
  onlineNow: number;
  /** 오늘 누적 메시지 */
  messagesToday: number;
  /** 오늘 누적 AI 호출 */
  aiCallsToday: number;
  /** 직전 표본 대비 신규 메시지 (없으면 null) */
  messageDelta: number | null;
  /** 직전 표본 대비 신규 AI 호출 (없으면 null) */
  aiDelta: number | null;
}

/** 천 단위 구분 숫자 */
function formatNumber(n: number): string {
  return (n ?? 0).toLocaleString('ko-KR');
}

/** 바이트 → 사람이 읽기 쉬운 단위 */
function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  const formatted = i === 0 ? String(value) : value.toFixed(value >= 100 ? 0 : 1);
  return `${formatted} ${units[i]}`;
}

/**
 * 표본 배열 → 부드러운 스파크라인 SVG path.
 * 값은 0..max 로 정규화해 높이에 매핑한다. 표본이 1개뿐이면 직선을 그린다.
 */
function sparklinePath(values: number[], width: number, height: number, pad = 2): string {
  if (values.length === 0) return '';
  const max = Math.max(1, ...values);
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const step = values.length > 1 ? innerW / (values.length - 1) : 0;
  const points = values.map((v, i) => {
    const x = pad + step * i;
    const y = pad + innerH - (v / max) * innerH;
    return [x, y] as const;
  });
  if (points.length === 1) {
    const [, y] = points[0];
    return `M ${pad} ${y} L ${width - pad} ${y}`;
  }
  return points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(' ');
}

/** 스파크라인 아래 채움(area) path */
function sparklineAreaPath(values: number[], width: number, height: number, pad = 2): string {
  const line = sparklinePath(values, width, height, pad);
  if (!line) return '';
  return `${line} L ${width - pad} ${height - pad} L ${pad} ${height - pad} Z`;
}

interface SparklineProps {
  values: number[];
  color: string;
  width?: number;
  height?: number;
}

/** 인라인 SVG 스파크라인 (선 + 면 + 마지막 점) */
function Sparkline({ values, color, width = 280, height = 64 }: SparklineProps) {
  const gid = useMemo(() => `spark-${Math.random().toString(36).slice(2, 9)}`, []);
  if (values.length === 0) {
    return <div className="mon-empty">데이터 수집 중…</div>;
  }
  const line = sparklinePath(values, width, height);
  const area = sparklineAreaPath(values, width, height);
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? (width - 4) / (values.length - 1) : 0;
  const lastX = 2 + step * (values.length - 1);
  const lastY = 2 + (height - 4) - (values[values.length - 1] / max) * (height - 4);
  return (
    <svg
      className="mon-spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="추이 스파크라인"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lastX} cy={lastY} r="3" fill={color} />
    </svg>
  );
}

interface BarChartProps {
  values: number[];
  color: string;
  /** 막대 라벨(선택) — 값이 많으면 일부만 표시 */
  unit?: string;
}

/** div 막대 차트 (직전 표본 대비 증분 등 "구간 발생량" 표현용) */
function BarChart({ values, color, unit = '' }: BarChartProps) {
  if (values.length === 0) {
    return <div className="mon-empty">데이터 수집 중…</div>;
  }
  const max = Math.max(1, ...values);
  // 너무 많으면 최근 것 위주로 표시 (가벼움)
  const shown = values.slice(-30);
  return (
    <div className="mon-bars" aria-hidden>
      {shown.map((v, i) => {
        const h = Math.max(2, Math.round((v / max) * 100));
        return (
          <span
            key={i}
            className="mon-bar"
            style={{ height: `${h}%`, background: color }}
            title={`${v}${unit}`}
          />
        );
      })}
    </div>
  );
}

export default function Monitoring() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [samples, setSamples] = useState<Sample[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [live, setLive] = useState(false);

  const mountedRef = useRef(true);
  // 직전 스냅샷 (증분 계산용) — 렌더와 무관하므로 ref 로 보관
  const prevRef = useRef<AdminStats | null>(null);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (opts.silent) setRefreshing(true);
    try {
      const res = await adminApi.stats();
      if (!mountedRef.current) return;
      const s = res.stats;
      setStats(s);
      setLive(true);
      setError(null);

      // 추이 표본 누적 (직전 스냅샷과 비교해 증분 계산)
      const prev = prevRef.current;
      // 메시지/AI 누적값은 자정에 리셋(오늘 카운트)되므로 음수 증분은 0으로 클램프
      const messageDelta = prev ? Math.max(0, s.messagesToday - prev.messagesToday) : null;
      const aiDelta = prev ? Math.max(0, s.aiCallsToday - prev.aiCallsToday) : null;
      const sample: Sample = {
        at: s.generatedAt || Date.now(),
        onlineNow: s.onlineNow,
        messagesToday: s.messagesToday,
        aiCallsToday: s.aiCallsToday,
        messageDelta,
        aiDelta,
      };
      prevRef.current = s;
      setSamples((prevSamples) => {
        const next = [...prevSamples, sample];
        return next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next;
      });
    } catch (e) {
      if (!mountedRef.current) return;
      setLive(false);
      // 401 은 api 클라이언트가 세션을 폐기하므로 가드가 로그인으로 보낸다.
      if (e instanceof AdminApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : '모니터링 데이터를 불러오지 못했습니다.');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // 최초 로드 + 5초 폴링 (일시정지 또는 백그라운드 탭이면 생략)
  useEffect(() => {
    mountedRef.current = true;
    void load();
    const timer = window.setInterval(() => {
      if (paused) return;
      if (document.visibilityState !== 'visible') return;
      void load({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
    };
  }, [load, paused]);

  // 파생 시계열. 첫 표본은 직전 비교 대상이 없어 증분이 null 이므로 막대 차트에서 제외한다.
  const onlineSeries = useMemo(() => samples.map((s) => s.onlineNow), [samples]);
  const messageDeltaSeries = useMemo(
    () => samples.filter((s) => s.messageDelta != null).map((s) => s.messageDelta as number),
    [samples],
  );
  const aiDeltaSeries = useMemo(
    () => samples.filter((s) => s.aiDelta != null).map((s) => s.aiDelta as number),
    [samples],
  );

  // 구간 집계 (관측 창 동안의 합/평균/피크)
  const windowMessages = messageDeltaSeries.reduce((a, b) => a + b, 0);
  const windowAi = aiDeltaSeries.reduce((a, b) => a + b, 0);
  const peakOnline = onlineSeries.length ? Math.max(...onlineSeries) : 0;
  const avgOnline = onlineSeries.length
    ? Math.round(onlineSeries.reduce((a, b) => a + b, 0) / onlineSeries.length)
    : 0;

  // 관측 창의 실제 경과 시간(분) — 분당 처리량 근사에 사용
  const windowMinutes = useMemo(() => {
    if (samples.length < 2) return 0;
    const span = samples[samples.length - 1].at - samples[0].at;
    return span > 0 ? span / 60_000 : 0;
  }, [samples]);
  const messagesPerMin = windowMinutes > 0 ? Math.round(windowMessages / windowMinutes) : 0;
  const aiPerMin = windowMinutes > 0 ? (windowAi / windowMinutes).toFixed(1) : '0';

  // 마지막 표본 대비 증분(가장 최근 5초 구간 발생량)
  const lastSample = samples[samples.length - 1];
  const lastMsgDelta = lastSample?.messageDelta ?? null;
  const lastAiDelta = lastSample?.aiDelta ?? null;

  const generatedLabel = stats ? `${formatKakaoTime(stats.generatedAt)} 갱신` : '';

  return (
    <>
      <TopBar
        title="모니터링"
        subtitle="접속자·메시지량·AI 사용량 실시간 추이"
        actions={
          <div className="row">
            <Badge tone={live && !paused ? 'success' : paused ? 'warning' : 'danger'}>
              {paused ? '일시정지' : live ? '● LIVE' : '연결 끊김'}
            </Badge>
            {generatedLabel && <span className="muted">{generatedLabel}</span>}
            {refreshing && <Spinner />}
            <Button variant="secondary" size="sm" onClick={() => setPaused((p) => !p)}>
              {paused ? '재개' : '일시정지'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void load({ silent: true })}
              loading={refreshing && !loading}
            >
              새로고침
            </Button>
          </div>
        }
      />
      <div className="admin-content">
        {loading && !stats ? (
          <Spinner center />
        ) : error && !stats ? (
          <Card title="오류">
            <div className="muted" style={{ marginBottom: 'var(--space-md)' }}>
              {error}
            </div>
            <Button variant="primary" onClick={() => void load()}>
              다시 시도
            </Button>
          </Card>
        ) : (
          <>
            {error && (
              <div className="muted" style={{ marginBottom: 'var(--space-lg)' }}>
                ⚠ 실시간 갱신 일시 실패: {error}
              </div>
            )}

            {/* 핵심 실시간 지표 */}
            {stats && (
              <section className="page-section">
                <div className="stat-grid">
                  <StatCard
                    label="현재 접속"
                    icon="🟢"
                    value={formatNumber(stats.onlineNow)}
                    hint={`피크 ${formatNumber(peakOnline)} · 평균 ${formatNumber(avgOnline)} (관측창)`}
                  />
                  <StatCard
                    label="오늘 메시지"
                    icon="✉️"
                    value={formatNumber(stats.messagesToday)}
                    hint={
                      lastMsgDelta != null
                        ? `최근 5초 +${formatNumber(lastMsgDelta)} · ~${messagesPerMin}/분`
                        : '추세 수집 중'
                    }
                  />
                  <StatCard
                    label="오늘 AI 호출"
                    icon="🤖"
                    value={formatNumber(stats.aiCallsToday)}
                    hint={
                      lastAiDelta != null
                        ? `최근 5초 +${formatNumber(lastAiDelta)} · ~${aiPerMin}/분`
                        : '추세 수집 중'
                    }
                  />
                  <StatCard
                    label="활성 사용자"
                    icon="✅"
                    value={formatNumber(stats.activeUsers)}
                    hint={`전체 ${formatNumber(stats.totalUsers)}명 중`}
                  />
                </div>
              </section>
            )}

            {/* 추이 차트 (스파크라인 + 막대) */}
            <section className="page-section mon-charts">
              <Card title="접속자 추이">
                <div className="mon-chart-head">
                  <span className="mon-chart-now" style={{ color: 'var(--color-online)' }}>
                    {formatNumber(stats?.onlineNow ?? 0)}
                  </span>
                  <span className="muted">명 접속 중 · 관측 {samples.length}표본</span>
                </div>
                <Sparkline values={onlineSeries} color="var(--color-online)" />
                <div className="mon-axis">
                  <span>{onlineSeries.length ? formatKakaoTime(samples[0].at) : ''}</span>
                  <span>지금</span>
                </div>
              </Card>

              <Card title="메시지량 추이 (5초 구간)">
                <div className="mon-chart-head">
                  <span className="mon-chart-now" style={{ color: 'var(--color-link)' }}>
                    {formatNumber(windowMessages)}
                  </span>
                  <span className="muted">건 / 관측창 · ~{messagesPerMin}건/분</span>
                </div>
                <BarChart values={messageDeltaSeries} color="var(--color-link)" unit="건" />
                <div className="mon-axis">
                  <span>구간당 신규 메시지</span>
                  <span>지금</span>
                </div>
              </Card>

              <Card title="AI 사용량 추이 (5초 구간)">
                <div className="mon-chart-head">
                  <span className="mon-chart-now" style={{ color: 'var(--color-admin-primary)' }}>
                    {formatNumber(windowAi)}
                  </span>
                  <span className="muted">회 / 관측창 · ~{aiPerMin}회/분</span>
                </div>
                <BarChart values={aiDeltaSeries} color="var(--color-admin-primary)" unit="회" />
                <div className="mon-axis">
                  <span>구간당 Claude 호출</span>
                  <span>지금</span>
                </div>
              </Card>
            </section>

            {/* 시스템 상태 */}
            {stats && (
              <section className="page-section">
                <Card title="시스템 상태">
                  <ul className="mon-status">
                    <li className="mon-status__item">
                      <span className="mon-status__dot" data-tone={live ? 'ok' : 'down'} />
                      <span className="mon-status__name">API 서버</span>
                      <span className="mon-status__val">
                        {live ? (
                          <Badge tone="success">정상</Badge>
                        ) : (
                          <Badge tone="danger">응답 없음</Badge>
                        )}
                      </span>
                    </li>
                    <li className="mon-status__item">
                      <span
                        className="mon-status__dot"
                        data-tone={paused ? 'warn' : live ? 'ok' : 'down'}
                      />
                      <span className="mon-status__name">실시간 폴링</span>
                      <span className="mon-status__val muted">
                        {paused ? '일시정지됨' : `${POLL_INTERVAL_MS / 1000}초 주기`}
                      </span>
                    </li>
                    <li className="mon-status__item">
                      <span className="mon-status__dot" data-tone="ok" />
                      <span className="mon-status__name">채팅방</span>
                      <span className="mon-status__val muted">{formatNumber(stats.totalRooms)}개 운영</span>
                    </li>
                    <li className="mon-status__item">
                      <span
                        className="mon-status__dot"
                        data-tone={stats.storageBytes > 0 ? 'ok' : 'warn'}
                      />
                      <span className="mon-status__name">파일 저장소 (R2)</span>
                      <span className="mon-status__val muted">{formatBytes(stats.storageBytes)} 사용</span>
                    </li>
                    <li className="mon-status__item">
                      <span className="mon-status__dot" data-tone="ok" />
                      <span className="mon-status__name">누적 메시지</span>
                      <span className="mon-status__val muted">{formatNumber(stats.messagesTotal)}건</span>
                    </li>
                    <li className="mon-status__item">
                      <span className="mon-status__dot" data-tone="ok" />
                      <span className="mon-status__name">데이터 기준 시각</span>
                      <span className="mon-status__val muted">{formatKakaoTime(stats.generatedAt)}</span>
                    </li>
                  </ul>
                </Card>
              </section>
            )}
          </>
        )}
      </div>

      {/* 모니터링 전용 보조 스타일 (인라인 차트) */}
      <style>{`
        .mon-charts {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: var(--space-lg);
        }
        .mon-chart-head {
          display: flex;
          align-items: baseline;
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }
        .mon-chart-now {
          font-size: 26px;
          font-weight: 700;
          line-height: 1;
        }
        .mon-chart-head .muted {
          font-size: var(--font-size-sm);
        }
        .mon-spark {
          display: block;
          width: 100%;
          height: 64px;
        }
        .mon-bars {
          display: flex;
          align-items: flex-end;
          gap: 3px;
          height: 64px;
          width: 100%;
        }
        .mon-bar {
          flex: 1 1 0;
          min-width: 2px;
          border-radius: 2px 2px 0 0;
          opacity: 0.85;
          transition: height 0.3s ease;
        }
        .mon-empty {
          height: 64px;
          display: grid;
          place-items: center;
          color: var(--color-text-tertiary);
          font-size: var(--font-size-sm);
          border: 1px dashed var(--color-divider);
          border-radius: var(--radius-md);
        }
        .mon-axis {
          display: flex;
          justify-content: space-between;
          margin-top: var(--space-sm);
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
        }
        .mon-status {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        .mon-status__item {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: 12px 0;
          border-bottom: 1px solid var(--color-divider);
          font-size: var(--font-size-sm);
        }
        .mon-status__item:last-child {
          border-bottom: none;
        }
        .mon-status__dot {
          width: 9px;
          height: 9px;
          border-radius: var(--radius-full);
          flex-shrink: 0;
          background: var(--color-text-tertiary);
        }
        .mon-status__dot[data-tone='ok'] {
          background: var(--color-online);
          box-shadow: 0 0 0 3px rgba(51, 199, 90, 0.16);
        }
        .mon-status__dot[data-tone='warn'] {
          background: var(--color-unread-yellow);
          box-shadow: 0 0 0 3px rgba(245, 195, 66, 0.16);
        }
        .mon-status__dot[data-tone='down'] {
          background: var(--color-badge-red);
          box-shadow: 0 0 0 3px rgba(255, 59, 48, 0.16);
        }
        .mon-status__name {
          font-weight: 500;
        }
        .mon-status__val {
          margin-left: auto;
        }
      `}</style>
    </>
  );
}
